// frontend/src/components/RequireRole.tsx
import type { ReactNode } from 'react'
import type { Identity } from '@/lib/auth'

const isDev =
  (import.meta as any)?.env?.MODE === 'development' ||
  (typeof location !== 'undefined' &&
    (location.hostname === 'localhost' || location.hostname === '127.0.0.1'))

function hasRole(identity: Identity | null, ...need: string[]): boolean {
  if (!identity) return false
  const roles = identity.roles ?? []
  return need.every((r) => roles.includes(r))
}

type Props = {
  /** require a single role */
  role?: string
  /** allow if user has ANY of these roles */
  anyOf?: string[]
  /** require ALL of these roles */
  allOf?: string[]
  /** caller-provided identity (can be null while loading) */
  identity: Identity | null
  /** optional custom fallback UI (used on denied) */
  fallback?: ReactNode
  children: ReactNode
}

/**
 * Render children only if role checks pass.
 * - While identity is loading (null): show a small loading hint (or bypass in dev).
 * - In dev, never block the UI (bypass) to keep demos smooth until OIDC/RBAC is wired.
 * - In prod, show a 403 panel if checks fail.
 */
export default function RequireRole({
  role,
  anyOf,
  allOf,
  identity,
  fallback,
  children,
}: Props) {
  // While identity is loading, avoid flashing a 403.
  if (identity == null) {
    // In dev, just render children so local demo isn’t blocked.
    if (isDev) return <>{children}</>
    // In non-dev, show a tiny loading hint instead of 403.
    return (
      <div className="p-4 text-sm text-zinc-600">
        Loading&hellip;
      </div>
    )
  }

  const requiredAll = allOf ?? (role ? [role] : [])
  const okAll = requiredAll.length ? hasRole(identity, ...requiredAll) : true
  const okAny = (anyOf && anyOf.length) ? anyOf.some((r) => hasRole(identity, r)) : true
  const allowed = okAll && okAny

  // In dev, bypass RBAC to keep demo flows unblocked.
  if (!allowed && isDev) {
    return <>{children}</>
  }

  if (!allowed) {
    return (
      fallback ?? (
        <div className="p-6 border rounded bg-red-50 text-red-700">
          <h3 className="font-semibold">403 – Forbidden</h3>
          <p className="text-sm">
            You don’t have access to this feature. Request access from your admin.
          </p>
        </div>
      )
    )
  }

  return <>{children}</>
}